// frontend/src/pages/Blog.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Search, Tag, Calendar, Eye } from 'lucide-react';

function BlogCard({ post }) {
  return (
    <Link to={`/blogs/${post.slug}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      <article style={{
        background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
        overflow: 'hidden', transition: 'var(--transition)', cursor: 'pointer'
      }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = 'var(--shadow-lg)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>

        {post.coverImage && (
          <div style={{ height: 200, overflow: 'hidden', background: '#f3f4f6' }}>
            <img src={post.coverImage} alt={post.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        )}
        {!post.coverImage && (
          <div style={{ height: 140, background: 'linear-gradient(135deg, #E8F1FF, #ffe0e0)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48 }}>
            📝
          </div>
        )}

        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={{ background: '#E8F1FF', color: 'var(--primary)', padding: '3px 10px', borderRadius: 50, fontSize: '0.75rem', fontWeight: 600 }}>
              {post.category}
            </span>
            {post.tags && post.tags.slice(0, 2).map(tag => (
              <span key={tag} style={{ background: '#f3f4f6', color: 'var(--text-muted)', padding: '3px 10px', borderRadius: 50, fontSize: '0.75rem' }}>
                #{tag}
              </span>
            ))}
          </div>

          <h3 style={{ fontFamily: 'Sora, sans-serif', fontSize: '1.05rem', fontWeight: 700, marginBottom: 8, lineHeight: 1.35 }}>
            {post.title}
          </h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.87rem', lineHeight: 1.55, marginBottom: 14,
            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {post.excerpt}
          </p>

          <div style={{ display: 'flex', gap: 16, color: '#94a3b8', fontSize: '0.78rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Calendar size={12} />
              {new Date(post.publishedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Eye size={12} /> {post.views || 0} views
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}

export default function BlogList() {
  const [posts, setPosts]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [pages, setPages]     = useState(1);
  const [search, setSearch]   = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);

  const CATEGORIES = ['General', 'Tutorial', 'Tips & Tricks', 'News', 'Product Update'];

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 9 };
      if (search)   params.search   = search;
      if (category) params.category = category;
      const res = await axios.get('/api/blog', { params });
      setPosts(res.data.posts);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch { setPosts([]); }
    setLoading(false);
  };

  useEffect(() => { fetchPosts(); }, [page, category]);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); fetchPosts(); };

  return (
    <div>
      {/* Hero */}
      <section className="hero" style={{ padding: '48px 20px 36px' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>✍️</div>
        <h1 style={{ fontSize: 'clamp(1.8rem,4vw,2.6rem)', marginBottom: 10 }}>ILoveConvert Blog</h1>
        <p style={{ color: 'var(--text-muted)', marginBottom: 28 }}>Tips, tutorials and updates about file conversion</p>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10, maxWidth: 480, margin: '0 auto', width: '100%' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search articles..."
              style={{ width: '100%', padding: '12px 14px 12px 40px', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)', fontFamily: 'inherit', fontSize: '0.95rem', outline: 'none' }} />
          </div>
          <button type="submit" className="btn btn-primary">Search</button>
        </form>
      </section>

      <div className="container" style={{ padding: '40px 20px' }}>
        {/* Category filter */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 32 }}>
          {['All', ...CATEGORIES].map(cat => (
            <button key={cat}
              onClick={() => { setCategory(cat === 'All' ? '' : cat); setPage(1); }}
              style={{
                padding: '7px 18px', borderRadius: 50, border: '2px solid',
                borderColor: (category === cat || (cat === 'All' && !category)) ? 'var(--primary)' : 'var(--border)',
                background: (category === cat || (cat === 'All' && !category)) ? 'var(--primary)' : 'white',
                color: (category === cat || (cat === 'All' && !category)) ? 'white' : 'var(--text-muted)',
                cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', fontFamily: 'inherit', transition: 'var(--transition)'
              }}>
              {cat}
            </button>
          ))}
        </div>

        <p style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: '0.88rem' }}>
          {total} article{total !== 1 ? 's' : ''} found
        </p>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}><div className="spinner" /></div>
        ) : posts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
            <p>No posts found. Check back soon!</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
            {posts.map(post => <BlogCard key={post._id} post={post} />)}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 48 }}>
            <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1} className="btn btn-outline btn-sm">← Prev</button>
            {Array.from({ length: pages }, (_, i) => (
              <button key={i + 1} onClick={() => setPage(i + 1)}
                className={`btn btn-sm ${page === i + 1 ? 'btn-primary' : 'btn-outline'}`}>
                {i + 1}
              </button>
            ))}
            <button onClick={() => setPage(p => Math.min(p + 1, pages))} disabled={page === pages} className="btn btn-outline btn-sm">Next →</button>
          </div>
        )}
      </div>

    </div>
  );
}
